import { 
  TrendingDown, 
  Shield, 
  Sun, 
  BarChart3,
  ArrowRight,
  CheckCircle2,
  Zap,
  Activity,
  Clock,
  AlertTriangle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

export const UseCases = ({ onContactClick }) => {
  const useCases = [
    {
      id: 'peak-shaving',
      title: 'Peak Shaving',
      subtitle: 'Lastspitzenkappung',
      icon: TrendingDown,
      iconBg: 'bg-emerald-50',
      iconColor: 'text-[#10B981]',
      description: 'Reduzieren Sie Ihre Netzentgelte durch intelligente Kappung von Lastspitzen.',
      problem: 'Die Netzentgelte werden maßgeblich durch die höchste gemessene Lastspitze im Jahr (15-Minuten-Mittelwert) bestimmt. Im Krankenhaus entstehen hohe Spitzen etwa durch gleichzeitiges Anschalten vieler Geräte morgens, Klimaanlagen im Sommer oder MRT-Scans.',
      solution: 'Das BESS erkennt drohende Lastspitzen automatisch und speist Energie ein, bevor kritische Schwellenwerte überschritten werden. So wird die gemessene Maximallast dauerhaft gesenkt.',
      benefits: [
        'Netzentgelte sinken um 8-15%',
        'Automatische Steuerung ohne manuellen Eingriff',
        'Sofortige Wirkung ab Inbetriebnahme',
        'Kein Einfluss auf den Krankenhausbetrieb',
      ],
      example: {
        before: '2.500 kW Lastspitze',
        after: '2.000 kW Lastspitze',
        saving: '~60.000 €/Jahr',
      },
    },
    {
      id: 'notstrom',
      title: 'Notstrom',
      subtitle: 'Versorgungssicherheit',
      icon: Shield,
      iconBg: 'bg-red-50',
      iconColor: 'text-red-500',
      description: 'Unterbrechungsfreie Stromversorgung für kritische medizinische Systeme.',
      problem: 'Bei einem Netzausfall startet das Diesel-Notstromaggregat erst nach 10-15 Sekunden. Diese Unterbrechung kann kritische Systeme wie Beatmungsgeräte, OP-Beleuchtung oder Intensivstations-Monitoring gefährden.',
      solution: 'Das BESS übernimmt bei Netzausfall binnen Millisekunden (< 20 ms) die Versorgung und überbrückt die Zeit, bis das Notstromaggregat voll hochgefahren ist. So entsteht eine lückenlose Stromversorgung.',
      benefits: [
        'Unterbrechungsfreie Versorgung (< 20 ms)',
        'Brücke zum Notstromaggregat',
        'Schutz für alle kritischen Systeme',
        'Erfüllung höchster Sicherheitsstandards',
      ],
      example: {
        before: '10-15 Sek. Unterbrechung',
        after: '0 ms Unterbrechung',
        saving: 'Unschätzbarer Schutz',
      },
    },
    {
      id: 'pv-integration',
      title: 'PV-Integration',
      subtitle: 'Eigenverbrauchsoptimierung',
      icon: Sun,
      iconBg: 'bg-amber-50',
      iconColor: 'text-amber-500',
      description: 'Maximieren Sie die Nutzung Ihres selbst erzeugten Solarstroms.',
      problem: 'Ohne Speicher wird überschüssiger PV-Strom zu niedrigen Einspeisevergütungen (6-8 ct/kWh) ins Netz abgegeben, während zu anderen Zeiten teurer Netzstrom (18-25 ct/kWh) bezogen werden muss.',
      solution: 'Das BESS speichert überschüssigen Solarstrom und gibt ihn ab, wenn die PV-Anlage weniger produziert als benötigt – zum Beispiel abends oder bei Bewölkung. So nutzen Sie bis zu 90% Ihres Solarstroms selbst.',
      benefits: [
        'Eigenverbrauchsquote bis 90%',
        'Ersparnis von 8-12 ct/kWh',
        'Schnellere Amortisation der PV-Anlage',
        'Mehr Unabhängigkeit vom Stromnetz',
      ],
      example: {
        before: '40% Eigenverbrauch',
        after: '90% Eigenverbrauch',
        saving: '~40.000 €/Jahr',
      },
    },
    {
      id: 'arbitrage',
      title: 'Arbitrage',
      subtitle: 'Tarifoptimierung',
      icon: BarChart3,
      iconBg: 'bg-blue-50',
      iconColor: 'text-blue-500',
      description: 'Nutzen Sie Preisunterschiede zwischen Hoch- und Niedertarifzeiten.',
      problem: 'Stromtarife variieren je nach Tageszeit erheblich. In Spitzenzeiten (werktags 8-20 Uhr) ist Strom deutlich teurer als nachts oder am Wochenende – ein ungenutztes Einsparpotenzial.',
      solution: 'Das BESS lädt in günstigen Niedertarifzeiten (nachts, Wochenende) und entlädt in teuren Hochtarifzeiten. So beziehen Sie immer zum günstigsten Preis.',
      benefits: [
        'Einsparung von 3-8% der Stromkosten',
        'Automatische Tarifoptimierung',
        'Flexibel bei Tarifänderungen',
        'Kombinierbar mit anderen Anwendungsfällen',
      ],
      example: {
        before: 'Durchschnittstarif 19 ct/kWh',
        after: 'Effektiver Tarif 17 ct/kWh',
        saving: '~30.000 €/Jahr',
      },
    },
  ];

  return (
    <main className="pt-16 lg:pt-20">
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-slate-50 to-white" data-testid="usecases-hero">
        <div className="container-main">
          <div className="max-w-3xl">
            <span className="text-[#10B981] font-medium text-sm uppercase tracking-wide">Anwendungsfälle</span>
            <h1 className="heading-xl text-[#0F172A] mt-2 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Vier Wege zu mehr Effizienz und Sicherheit
            </h1>
            <p className="text-lg text-slate-600 mb-8">
              Ein BESS im Krankenhaus erfüllt mehrere Funktionen gleichzeitig – von der Kostenreduktion 
              bis zur kritischen Versorgungssicherheit. Entdecken Sie die Anwendungsfälle.
            </p>
          </div>
        </div>
      </section>

      {/* Use Cases Grid Overview */}
      <section className="section-padding bg-white" data-testid="usecases-overview">
        <div className="container-main">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {useCases.map((useCase) => (
              <Card key={useCase.id} className="card-feature group cursor-pointer" data-testid={`usecase-card-${useCase.id}`}>
                <CardContent className="p-6">
                  <div className={`w-12 h-12 ${useCase.iconBg} rounded-lg flex items-center justify-center mb-4`}>
                    <useCase.icon className={`w-6 h-6 ${useCase.iconColor}`} />
                  </div>
                  <h3 className="text-lg font-semibold text-[#0F172A] mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {useCase.title}
                  </h3>
                  <p className="text-sm text-slate-500 mb-3">{useCase.subtitle}</p>
                  <p className="text-sm text-slate-600">{useCase.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Use Cases with Tabs */}
      <section className="section-padding bg-slate-50" data-testid="usecases-detailed">
        <div className="container-main">
          <div className="text-center mb-12">
            <h2 className="heading-lg text-[#0F172A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Anwendungsfälle im Detail
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Wählen Sie einen Anwendungsfall, um mehr über Problem, Lösung und konkrete Vorteile zu erfahren.
            </p>
          </div>

          <Tabs defaultValue="peak-shaving" className="w-full">
            <TabsList className="grid grid-cols-2 lg:grid-cols-4 mb-8 h-auto gap-2 bg-transparent">
              {useCases.map((useCase) => (
                <TabsTrigger 
                  key={useCase.id} 
                  value={useCase.id}
                  className="flex items-center gap-2 py-3 px-4 data-[state=active]:bg-white data-[state=active]:shadow-md rounded-lg border border-slate-200 data-[state=active]:border-emerald-200"
                  data-testid={`tab-${useCase.id}`}
                >
                  <useCase.icon className={`w-4 h-4 ${useCase.iconColor}`} />
                  <span className="hidden sm:inline">{useCase.title}</span>
                </TabsTrigger>
              ))}
            </TabsList>

            {useCases.map((useCase) => (
              <TabsContent key={useCase.id} value={useCase.id} className="mt-0">
                <Card className="bg-white">
                  <CardHeader className="border-b">
                    <div className="flex items-center gap-4">
                      <div className={`w-14 h-14 ${useCase.iconBg} rounded-lg flex items-center justify-center`}>
                        <useCase.icon className={`w-7 h-7 ${useCase.iconColor}`} />
                      </div>
                      <div>
                        <CardTitle className="text-2xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
                          {useCase.title}
                        </CardTitle>
                        <p className="text-slate-500">{useCase.subtitle}</p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="p-6 lg:p-8">
                    <div className="grid lg:grid-cols-2 gap-8">
                      {/* Left Column */}
                      <div className="space-y-6">
                        {/* Problem */}
                        <div className="bg-red-50 rounded-xl p-6 border border-red-100">
                          <div className="flex items-center gap-2 mb-3">
                            <AlertTriangle className="w-5 h-5 text-red-500" />
                            <h4 className="font-semibold text-red-800" style={{ fontFamily: 'Outfit, sans-serif' }}>
                              Die Herausforderung
                            </h4>
                          </div>
                          <p className="text-red-700 text-sm leading-relaxed">
                            {useCase.problem}
                          </p>
                        </div>

                        {/* Solution */}
                        <div className="bg-emerald-50 rounded-xl p-6 border border-emerald-100">
                          <div className="flex items-center gap-2 mb-3">
                            <CheckCircle2 className="w-5 h-5 text-[#10B981]" />
                            <h4 className="font-semibold text-emerald-800" style={{ fontFamily: 'Outfit, sans-serif' }}>
                              Die Lösung
                            </h4>
                          </div>
                          <p className="text-emerald-700 text-sm leading-relaxed">
                            {useCase.solution}
                          </p>
                        </div>
                      </div>

                      {/* Right Column */}
                      <div className="space-y-6">
                        {/* Benefits */}
                        <div>
                          <h4 className="font-semibold text-[#0F172A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            Ihre Vorteile
                          </h4>
                          <ul className="space-y-3">
                            {useCase.benefits.map((benefit, index) => (
                              <li key={index} className="flex items-start gap-3">
                                <CheckCircle2 className="w-5 h-5 text-[#10B981] flex-shrink-0 mt-0.5" />
                                <span className="text-slate-600">{benefit}</span>
                              </li>
                            ))}
                          </ul>
                        </div>

                        {/* Example */}
                        <div className="bg-slate-100 rounded-xl p-6">
                          <h4 className="font-semibold text-[#0F172A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            Beispielrechnung
                          </h4>
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <span className="text-slate-500 text-sm">Vorher:</span>
                              <span className="font-medium text-slate-700">{useCase.example.before}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-slate-500 text-sm">Mit BESS:</span>
                              <span className="font-medium text-[#10B981]">{useCase.example.after}</span>
                            </div>
                            <div className="border-t border-slate-200 pt-3 flex items-center justify-between">
                              <span className="text-slate-500 text-sm">Einsparung:</span>
                              <span className="font-bold text-[#10B981] text-lg">{useCase.example.saving}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      {/* Combined Benefits */}
      <section className="section-padding bg-white" data-testid="combined-benefits">
        <div className="container-main">
          <div className="text-center mb-12">
            <h2 className="heading-lg text-[#0F172A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Kombinierte Anwendung: Maximaler Nutzen
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Die wahre Stärke eines BESS liegt in der gleichzeitigen Nutzung mehrerer Anwendungsfälle.
            </p>
          </div>

          <div className="bg-slate-50 rounded-2xl p-6 lg:p-10">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="text-center">
                <div className="text-3xl font-bold text-[#10B981] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  15-25%
                </div>
                <p className="text-sm text-slate-600">Gesamteinsparung möglich</p>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-[#0F172A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  4-6 Jahre
                </div>
                <p className="text-sm text-slate-600">Typische Amortisation</p>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-[#0F172A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  0 ms
                </div>
                <p className="text-sm text-slate-600">Umschaltzeit bei Ausfall</p>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-[#0F172A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  90%+
                </div>
                <p className="text-sm text-slate-600">PV-Eigenverbrauch</p>
              </div>
            </div>

            <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
              <p className="text-blue-800 text-sm text-center">
                <strong>Wichtig:</strong> Die intelligente Steuerung priorisiert automatisch den wirtschaftlich 
                sinnvollsten Einsatz des Speichers zu jedem Zeitpunkt.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-[#0F172A] text-white" data-testid="usecases-cta">
        <div className="container-main text-center">
          <h2 className="heading-lg mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Welche Anwendungsfälle passen zu Ihrem Krankenhaus?
          </h2>
          <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
            Wir analysieren Ihr Lastprofil und identifizieren die optimale Kombination für maximale Wirtschaftlichkeit.
          </p>
          <Button 
            onClick={onContactClick}
            className="bg-[#10B981] hover:bg-emerald-600 text-white px-8 py-4 rounded-md font-medium inline-flex items-center gap-2"
            data-testid="usecases-final-cta"
          >
            Kostenlose Analyse anfragen
            <ArrowRight className="w-5 h-5" />
          </Button>
        </div>
      </section>
    </main>
  );
};
